import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/components/ui/accordion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning } from "@fortawesome/pro-regular-svg-icons";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { cn } from "../utils";
import { useCoverageDisplayContext } from "../contexts/CoverageDisplayContext";
import { useAppLayerContext } from "../contexts/AppLayer";
import { Badge } from "./ui/Badge";
import { useBreedSpecificBadge } from "../hooks/useBreedSpecificBadge";
import { Policy } from "../types/Quote.interface";
import { BreedConditionData } from "../hooks/useHasBreedConditions";
import BreedConditionFacts from "./BreedConditionFacts";
import { IllnessSectionGrid } from "./IllnessSectionGrid";

interface CoverageIconCategoriesProps {
    hasAnyBreedConditions?: boolean;
    policies?: Policy[];
    breedConditionData?: BreedConditionData | null;
    showNotCovered?: boolean;
    onSwitchToAccidentAndIllness?: () => void;
}

export default function CoverageIconCategories(props: CoverageIconCategoriesProps) {
    const { hasAnyBreedConditions, policies, breedConditionData, showNotCovered, onSwitchToAccidentAndIllness } = props;
    const { accidentItems, illnessSections, getBreedSpecificBadge, featureConfig } = useCoverageDisplayContext();
    const { appState } = useAppLayerContext();
    const { activePetIndex, isMultiPetLinked } = appState;
    const activePet = policies?.[activePetIndex || 0];

    const { sectionHasBreedConditions } = useBreedSpecificBadge({
        isMultiPetLinked,
        policies,
        activePetBreed: activePet?.breedName,
        enabled: featureConfig.breedSpecific
    });

    const hasAccidentAndIllness = Object.keys(illnessSections).length > 0;
    const coverageIconColSize = "w-9 xs:w-12 sm:w-9 md:w-12 lg:w-9 xl:w-12";

    const accidentCoverageData = accidentItems.map(item => (
        <div key={item.title || item.id} className="flex flex-row items-center gap-2 text-content-secondary sm:gap-1">
            <div>{item.icon}</div>
            <div className="flex flex-col">
                <div className="text-sm font-bold xs:text-base sm:text-sm md:text-base lg:text-sm xl:text-base">{item.title}</div>
                {item.description && <p className="text-xs font-medium xs:text-sm sm:text-xs md:text-sm lg:text-xs xl:text-sm">{item.description}</p>}
                {featureConfig.breedSpecific && item.affects && getBreedSpecificBadge(item.affects)}
            </div>
        </div>
    ));

    const illnessCoverageData = Object.entries(illnessSections).map(([key, section]) => (
        <AccordionItem value={key} className="border-b-0" key={key}>
            <AccordionTrigger wrapperAsChild id={`accordion-trigger-${key}`} className="flex w-full py-0 hover:no-underline">
                <div className="flex flex-row items-center gap-2 text-left text-sm text-content-secondary xs:text-base sm:text-sm md:text-base lg:text-sm xl:text-base">
                    <div className={cn(coverageIconColSize, "relative")}>
                        {section.icon}
                        {featureConfig.breedSpecific && sectionHasBreedConditions(section) && (
                            <div className="absolute -right-2 -top-2 z-10">
                                <Badge variant="warning" className="p-0">
                                    <div className="flex size-5 items-center justify-center">
                                        <FontAwesomeIcon className="size-4" icon={faWarning} />
                                    </div>
                                </Badge>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:gap-1">
                        {section.title}
                        {featureConfig.breedSpecific && section.affects && getBreedSpecificBadge(section.affects)}
                    </div>
                </div>
            </AccordionTrigger>
            <AccordionContent aria-labelledby={`accordion-trigger-${key}`} className="grid gap-2 py-0 pt-2 sm:grid-cols-2">
                {section.includes?.map(({ title, affects }) => (
                    <div
                        key={title}
                        className="flex flex-row items-center gap-2 text-sm font-medium text-content-secondary xs:text-base sm:text-sm md:text-base md:leading-5 lg:text-sm xl:text-base xl:leading-5"
                    >
                        <div className={cn("flex shrink-0 items-center justify-center text-content-primary", coverageIconColSize)}>
                            <FontAwesomeIcon icon={faCheck} className="size-5" />
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {title}
                            {featureConfig.breedSpecific && affects && getBreedSpecificBadge(affects)}
                        </div>
                    </div>
                ))}
            </AccordionContent>
        </AccordionItem>
    ));

    return (
        <div className="flex w-full flex-col gap-4 px-4 sm:px-6 lg:px-0">
            <div className="grid gap-2 xs:gap-3 sm:grid-cols-2 sm:gap-2 md:gap-x-2 md:gap-y-3 lg:gap-y-2">{accidentCoverageData}</div>

            {/* Show not covered test */}
            {!hasAccidentAndIllness && showNotCovered && <IllnessSectionGrid onSwitchToAccidentAndIllness={onSwitchToAccidentAndIllness} />}

            {hasAccidentAndIllness && (
                <>
                    <div className="hidden border-t border-stroke-secondary pt-4 xl:grid xl:grid-cols-3 xl:gap-2">
                        {Object.entries(illnessSections).map(([key, section]) => (
                            <div key={key} className="flex flex-col gap-2" role="region" aria-labelledby={`illness-section-${key}-heading`}>
                                <div
                                    id={`illness-section-${key}-heading`}
                                    className="flex flex-row items-center gap-2 text-left text-sm font-bold text-content-secondary xs:text-base sm:text-sm md:text-base lg:text-sm xl:text-base"
                                >
                                    <div className={cn(coverageIconColSize)}>{section.icon}</div>
                                    <div className="flex flex-col">
                                        {section.title}
                                        {featureConfig.breedSpecific && section.affects && getBreedSpecificBadge(section.affects)}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    {section.includes?.map(({ title, affects }) => (
                                        <div
                                            key={title}
                                            className="flex flex-row items-center gap-2 text-sm font-medium text-content-secondary xs:text-base sm:text-sm md:text-base md:leading-5 lg:text-sm xl:text-base xl:leading-5"
                                        >
                                            <div className={cn("flex shrink-0 items-center justify-center text-content-primary", coverageIconColSize)}>
                                                <FontAwesomeIcon icon={faCheck} className="size-5" />
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {title}
                                                {featureConfig.breedSpecific && affects && getBreedSpecificBadge(affects)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <Accordion
                        type="multiple"
                        defaultValue={[]}
                        className="flex flex-col gap-2 border-t border-stroke-secondary pt-4 xs:gap-3 sm:gap-2 md:gap-3 lg:gap-2 xl:hidden"
                    >
                        {illnessCoverageData}
                    </Accordion>
                </>
            )}

            {hasAnyBreedConditions && breedConditionData && (
                <div className="lg:hidden">
                    <BreedConditionFacts breedConditionData={breedConditionData} />
                </div>
            )}
        </div>
    );
}
